import { animate } from 'motion'
import type { ViewHook } from 'phoenix_live_view'

function isHidden(el: HTMLElement | null) {
  if (el === null) {
    return true
  }

  return el.offsetParent === null
}

function isFlash(el: HTMLElement) {
  return el.dataset.component === 'flash'
}

function parseDuration(value: string | undefined, fallback: number) {
  if (value === undefined) {
    return fallback
  }

  if (value === 'Infinity') {
    return Number.POSITIVE_INFINITY
  }

  const parsed = Number.parseInt(value)

  return Number.isNaN(parsed) ? fallback : parsed
}

// number of flashes that aren't hidden
function flashCount() {
  let num = 0

  if (!isHidden(document.getElementById('server-error'))) {
    num += 1
  }

  if (!isHidden(document.getElementById('client-error'))) {
    num += 1
  }

  if (!isHidden(document.getElementById('flash-info'))) {
    num += 1
  }

  if (!isHidden(document.getElementById('flash-error'))) {
    num += 1
  }

  return num
}

// time in ms to wait before removal, but after animation
const removalTime = 5
// animation time in ms
const animationTime = 550
// whether flashes should be counted in maxItems
const maxItemsIgnoresFlashes = true
// gap in px between toasts
const gap = 15
const dismissEvent = 'live-toast-dismiss'
const clientToastEvent = 'live-toast:add'
const remainingSelector = '[data-live-toast-remaining]'

let lastTS: HTMLElement[] = []

type DismissTimer = {
  cancel: () => void
}

type ToastElement = HTMLElement & {
  order: number
  targetDestination: string
}

export type ClientToastOptions = {
  duration?: number | 'infinity'
  metadata?: Record<string, unknown>
  title?: string
}

type ClientToastRequest = {
  kind: string
  message: string
  options: ClientToastOptions
}

const dismissTimers = new WeakMap<object, DismissTimer>()

function asToastElement(el: HTMLElement): ToastElement {
  return el as ToastElement
}

export function addToast(
  kind: string,
  message: string,
  options: ClientToastOptions = {}
) {
  document
    .getElementById('toast-group')
    ?.dispatchEvent(
      new CustomEvent<ClientToastRequest>(clientToastEvent, {
        detail: { kind, message, options }
      })
    )
}

function doAnimations(
  this: ViewHook,
  animationDelayTime: number,
  maxItems: number,
  elToRemove?: HTMLElement
) {
  const ts: ToastElement[] = []
  let toasts = Array.from(
    document.querySelectorAll<ToastElement>(
      '#toast-group [phx-hook="LiveToast"]'
    )
  )
    .map(t => {
      if (isHidden(t)) {
        return null
      }

      return t
    })
    .filter(Boolean)
    // reverse
    .reverse()

  if (elToRemove) {
    toasts = toasts.filter(t => t !== elToRemove)
  }

  // Traverse through all toasts, in order they appear in the dom, for which they are NOT hidden, and assign el.order to
  // their index
  for (let i = 0; i < toasts.length; i++) {
    const toast = toasts[i]!
    if (isHidden(toast)) {
      continue
    }
    toast.order = i

    ts[i] = toast
  }

  // now loop through ts and animate each toast to its position
  for (let i = 0; i < ts.length; i++) {
    const max = maxItemsIgnoresFlashes ? maxItems + flashCount() : maxItems

    const toast = ts[i]

    let direction = ''

    if (
      toast.dataset.corner === 'bottom_left' ||
      toast.dataset.corner === 'bottom_center' ||
      toast.dataset.corner === 'bottom_right'
    ) {
      direction = '-'
    }

    // Calculate the translateY value with gap
    // now that they can be different heights, we need to actually caluclate the real heights and add them up.
    let val = 0

    for (let j = 0; j < toast.order; j++) {
      val += ts[j].offsetHeight + gap
    }

    // Calculate opacity based on position
    const opacity = toast.order > max ? 0 : 1 - (toast.order - max + 1)

    // also if this item moved past the max limit, disable click events on it
    if (toast.order >= max) {
      toast.classList.remove('pointer-events-auto')
    } else {
      toast.classList.add('pointer-events-auto')
    }

    const keyframes = { y: [`${direction}${val}px`], opacity: [opacity] }

    // if element is entering for the first time, start below the fold
    if (toast.order === 0 && lastTS.includes(toast) === false) {
      const val = toast.offsetHeight + gap
      const oppositeDirection = direction === '-' ? '' : '-'
      keyframes.y.unshift(`${oppositeDirection}${val}px`)

      keyframes.opacity.unshift(0)
    }

    toast.targetDestination = `${direction}${val}px`

    const duration = animationTime / 1000

    // as of right now this is not exposed to end users, but
    // it's 'plumbed out' if we want to make it so in the future
    const delayTime = Number.parseInt(this.el.dataset.delay || '0') / 1000

    animate(toast, keyframes, {
      duration,
      easing: [0.22, 1.0, 0.36, 1.0],
      delay: delayTime
    })
    toast.order += 1

    // decrease z-index
    toast.style.zIndex = (50 - toast.order).toString()

    // if this element moved past the max item limit, send the signal to remove it
    // should this be shorted than delay time?
    // also what about elements moving down when you close one?
    window.setTimeout(() => {
      if (toast.order > max) {
        this.pushEventTo(toastGroupTarget(toast), 'clear', { id: toast.id })
      }
    }, animationDelayTime + removalTime)

    lastTS = ts
  }
}

function toastGroupTarget(el: HTMLElement) {
  const streamContainer = el.closest<HTMLElement>('[phx-update="stream"]')
  const toastGroup = streamContainer?.parentElement

  return toastGroup || '#toast-group'
}

async function animateOut(this: ViewHook) {
  const toast = asToastElement(this.el)
  const val = (toast.order - 2) * 100 + (toast.order - 2) * gap

  let direction = ''

  if (
    this.el.dataset.corner === 'bottom_left' ||
    this.el.dataset.corner === 'bottom_center' ||
    this.el.dataset.corner === 'bottom_right'
  ) {
    direction = '-'
  }

  const animation = animate(
    this.el,
    { y: `${direction}${val}%`, opacity: 0 },
    {
      opacity: {
        duration: 0.2,
        easing: 'ease-out'
      },
      duration: 0.3,
      easing: 'ease-out'
    }
  )

  await animation.finished
}

async function dismissToast(
  this: ViewHook,
  animationDelayTime: number,
  maxItems: number
) {
  if (this.el.dataset.liveToastDismissing === 'true') {
    return
  }

  this.el.dataset.liveToastDismissing = 'true'
  dismissTimers.get(this)?.cancel()
  dismissTimers.delete(this)

  doAnimations.bind(this, animationDelayTime, maxItems, this.el)()
  await animateOut.bind(this)()

  this.pushEventTo(toastGroupTarget(this.el), 'clear', { id: this.el.id })
}

function isInteracting(el: HTMLElement) {
  return el.matches(':hover') || el.matches(':focus-within')
}

function renderRemaining(el: HTMLElement, remaining: number, paused: boolean) {
  const output = el.querySelector<HTMLElement>(remainingSelector)

  if (!output) {
    return
  }

  output.textContent = Math.ceil(remaining / 1000).toString()
  output.dataset.paused = paused.toString()
}

function startDismissTimer(
  this: ViewHook,
  duration: number,
  animationDelayTime: number,
  maxItems: number
) {
  let remaining = duration
  let startedAt: number | undefined
  let timer: number | undefined
  let displayTimer: number | undefined

  const currentRemaining = () => {
    if (startedAt === undefined) {
      return remaining
    }

    return Math.max(0, remaining - (performance.now() - startedAt))
  }

  const clearTimers = () => {
    if (timer !== undefined) {
      window.clearTimeout(timer)
      timer = undefined
    }

    if (displayTimer !== undefined) {
      window.clearInterval(displayTimer)
      displayTimer = undefined
    }
  }

  const pause = () => {
    if (startedAt === undefined) {
      return
    }

    remaining = currentRemaining()
    startedAt = undefined
    clearTimers()
    renderRemaining(this.el, remaining, true)
  }

  const resume = () => {
    if (timer || isInteracting(this.el)) {
      return
    }

    startedAt = performance.now()
    renderRemaining(this.el, remaining, false)

    if (this.el.querySelector(remainingSelector)) {
      displayTimer = window.setInterval(() => {
        renderRemaining(this.el, currentRemaining(), false)
      }, 100)
    }

    timer = window.setTimeout(async () => {
      clearTimers()
      remaining = 0
      startedAt = undefined
      renderRemaining(this.el, remaining, false)

      await dismissToast.bind(this)(animationDelayTime, maxItems)
    }, remaining + removalTime)
  }

  const cancel = () => {
    clearTimers()
    this.el.removeEventListener('mouseenter', pause)
    this.el.removeEventListener('focusin', pause)
    this.el.removeEventListener('mouseleave', resume)
    this.el.removeEventListener('focusout', resume)
  }

  this.el.addEventListener('mouseenter', pause)
  this.el.addEventListener('focusin', pause)
  this.el.addEventListener('mouseleave', resume)
  this.el.addEventListener('focusout', resume)

  dismissTimers.set(this, { cancel })
  resume()
}

// Create the Phoenix Hoook for live_toast.
// You can set custom animation durations.
export function createLiveToastHook(duration = 6000, maxItems = 3) {
  return {
    destroyed(this: ViewHook) {
      if (this.el.dataset.liveToastGroup === 'true') {
        return
      }

      dismissTimers.get(this)?.cancel()
      dismissTimers.delete(this)
      doAnimations.bind(this)(duration, maxItems)
    },
    updated(this: ViewHook) {
      if (this.el.dataset.liveToastGroup === 'true') {
        return
      }

      // animate to targetDestination in 0ms
      const toast = asToastElement(this.el)
      const keyframes = { y: [toast.targetDestination] }
      animate(this.el, keyframes, { duration: 0 })
    },
    mounted(this: ViewHook) {
      if (this.el.dataset.liveToastGroup === 'true') {
        const clientToastListener = (event: Event) => {
          const request = (event as CustomEvent<ClientToastRequest>).detail

          if (!request) {
            return
          }

          this.pushEventTo(this.el, 'add_toast', {
            kind: request.kind,
            message: request.message,
            options: request.options
          })
        }

        this.el.addEventListener(clientToastEvent, clientToastListener)

        return
      }

      this.el.addEventListener('show-error', async _event => {
        const delayTime = Number.parseInt(this.el.dataset.delay || '0')
        await new Promise(resolve => setTimeout(resolve, delayTime))

        // todo: in the future use this to execute the data-disconnected command
        // https://elixirforum.com/t/can-we-use-liveview-js-commands-inside-a-hook/67324/8

        // const command = this.el.getAttribute('data-disconnected')
        // this.liveSocket.execJS(this.el, command)

        // (don't want to do this quite yet because 1.0 is pretty new)
        // also repeat this on hide.

        this.el.removeAttribute('hidden')
        this.el.style.display = 'flex'
      })

      this.el.addEventListener('hide-error', async _event => {
        this.el.style.display = 'none'
      })

      // for the special flashes, check if they are visible, and if not, return early out of here.
      if (['server-error', 'client-error'].includes(this.el.id)) {
        if (isHidden(document.getElementById(this.el.id))) {
          return
        }
      }

      window.addEventListener('phx:clear-flash', e => {
        this.pushEvent('lv:clear-flash', {
          key: (e as CustomEvent<{ key: string }>).detail.key
        })
      })

      window.addEventListener('flash-leave', async event => {
        if (event.target === this.el) {
          // animate this flash sliding out
          doAnimations.bind(this, duration, maxItems, this.el)()
          await animateOut.bind(this)()
        }
      })

      this.el.addEventListener(dismissEvent, async event => {
        event.stopPropagation()

        await dismissToast.bind(this)(duration, maxItems)
      })

      window.addEventListener(`phx:${dismissEvent}`, async event => {
        const detail = (event as CustomEvent<{ id?: string; uuid?: string }>)
          .detail
        const id = detail.id || `toast-${detail.uuid}`

        if (id === this.el.id) {
          await dismissToast.bind(this)(duration, maxItems)
        }
      })

      // begin actually showing the toast through this call to the animation function
      doAnimations.bind(this)(duration, maxItems)

      const durationOverride = parseDuration(this.el.dataset.duration, duration)

      let flashDuration = undefined
      if (this.el.dataset.flashDuration !== undefined) {
        flashDuration = Number.parseInt(this.el.dataset.flashDuration)
      }

      // skip the removal code if this is a flash, if autoHideFlash is nullish
      if (isFlash(this.el) && !flashDuration) {
        return
      }

      // this could be condensed
      if (flashDuration) {
        // do stuff
        window.setTimeout(async () => {
          // animate this element sliding down, opacity to 0, with delay time
          await animateOut.bind(this)()

          const kind = this.el.dataset.kind

          if (kind) {
            this.pushEvent('lv:clear-flash', { key: kind })
          }
        }, flashDuration + removalTime)
      } else {
        if (Number.isFinite(durationOverride) && durationOverride > 0) {
          startDismissTimer.bind(this)(durationOverride, duration, maxItems)
        }
      }
    }
  }
}
